// 리스트 접기(sibling coalescing, ADR-0046). 같은 부모 밑에 "같은 종류"(kind+group+displayName)
// 형제가 COALESCE_MIN개 이상 모이면 — 거의 항상 배열/map으로 렌더된 리스트다 — 대표 1개만 남기고
// 나머지 형제와 그 서브트리를 목록에서 뺀 뒤, 대표에 "×N" 개수를 붙인다.
//
// 왜: 리스트는 100개 이상도 흔한데, 지금은 N개 노드를 다 그려 (1) 구조가 안 안정적이고(리스트가
// 늘면 그룹 폭이 요동쳐 옆 그룹까지 밀린다), (2) 노드 수 비례 비용을 치르며, (3) 똑같은 박스 100개는
// 정보 가치가 없다. 어차피 구조적으로 동일하니 "대표 하나 + ×N"으로 접어도 정보 손실이 없다.
// 이건 downfall(barycenter) 레이아웃의 안정성 토대이기도 하다 — 리스트 폭 요동이 최대 흔들림원이라
// 그걸 접으면 남는 구조 변화는 드문 이산 이벤트(모달/탭/라우트/데이터)뿐이 된다.
//
// 데이터 레이어(RenderNode)는 안 건드린다 — normalize가 만든 VisibleNode[] 위에서만 접어, 스키마·
// 인터랙션·id→Fiber 매핑을 그대로 둔다(대표는 실제 fiber id를 유지해 클릭/하이라이트가 그대로 됨).
import type { VisibleNode } from './normalize';

/** 같은 종류 형제가 이 수 이상이면 리스트로 보고 접는다. 그 미만은 의도적 소수 집합일 수 있어 둔다. */
export const COALESCE_MIN = 5;

export function coalesceListSiblings(nodes: VisibleNode[]): VisibleNode[] {
  // 부모별 자식 목록(트리 순서 유지). parentId=null(루트)도 하나의 버킷.
  const childrenByParent = new Map<number | null, VisibleNode[]>();
  for (const n of nodes) {
    const list = childrenByParent.get(n.parentId);
    if (list) list.push(n);
    else childrenByParent.set(n.parentId, [n]);
  }

  const dropped = new Set<number>(); // 목록에서 뺄 id(비대표 형제 + 그 서브트리 전부)
  const countByRep = new Map<number, number>(); // 대표 id → 접은 형제 수 N

  for (const siblings of childrenByParent.values()) {
    // (kind, group, displayName)로 같은 종류끼리 묶는다. 익명 노드는 이름이 의미 없어 제외.
    const runs = new Map<string, VisibleNode[]>();
    for (const s of siblings) {
      if (s.isAnonymous) continue;
      const key = `${s.kind} ${s.group} ${s.displayName}`;
      const arr = runs.get(key);
      if (arr) arr.push(s);
      else runs.set(key, [s]);
    }
    for (const members of runs.values()) {
      if (members.length < COALESCE_MIN) continue;
      // 대표 = 가장 작은 id. 리스트가 재정렬돼도(React key 변경) id는 안정적이라 대표가 안 바뀐다.
      let rep = members[0];
      for (const m of members) if (m.id < rep.id) rep = m;
      countByRep.set(rep.id, members.length);
      for (const m of members) {
        if (m.id !== rep.id) markSubtreeDropped(m.id, childrenByParent, dropped);
      }
    }
  }

  if (dropped.size === 0 && countByRep.size === 0) return nodes;

  const result: VisibleNode[] = [];
  for (const n of nodes) {
    if (dropped.has(n.id)) continue;
    const count = countByRep.get(n.id);
    result.push(count === undefined ? n : { ...n, coalescedCount: count });
  }
  return result;
}

function markSubtreeDropped(
  rootId: number,
  childrenByParent: Map<number | null, VisibleNode[]>,
  dropped: Set<number>,
): void {
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (dropped.has(id)) continue;
    dropped.add(id);
    const kids = childrenByParent.get(id);
    if (kids) for (const k of kids) stack.push(k.id);
  }
}
